// Vendored from frontend-registration/src/utils/eventTime.js — separate CRA build
// context, no cross-import. Keep in sync by hand if the source logic changes.
//
// `event.start_datetime`/`end_datetime` (and session times) come from the API as
// naive "YYYY-MM-DDTHH:MM:SS" strings with no UTC offset — they represent local
// wall-clock time IN the event's own `timezone` column, not UTC and not the
// viewer's browser zone. `new Date(naiveIso)` lets the BROWSER guess the source
// zone, which silently produces a different (wrong) instant per viewer. Resolve
// the real UTC instant using the event's authored zone before ever formatting.

function getZoneOffsetMs(timeZone, date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(date).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const asUtc = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    +parts.hour, +parts.minute, +parts.second,
  );
  return asUtc - date.getTime();
}

// Interpret a naive datetime string as wall-clock time in `sourceTz` and return
// the correct absolute instant, independent of the browser's own local timezone.
export function resolveEventInstant(naiveIso, sourceTz) {
  const utcGuess = new Date(naiveIso.endsWith('Z') ? naiveIso : `${naiveIso}Z`);
  if (!sourceTz || sourceTz === 'UTC') return utcGuess;
  const offsetMs = getZoneOffsetMs(sourceTz, utcGuess);
  return new Date(utcGuess.getTime() - offsetMs);
}

// Best-effort viewer-local IANA zone, falling back to `fallback` when the API is
// unavailable (very old browsers) or returns something unusable.
export function detectViewerTimezone(fallback) {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || fallback;
  } catch {
    return fallback;
  }
}

// US abbreviations are unambiguous to this audience; every other zone is shown
// as an explicit offset ("GMT+5:30"), never an abbreviation like IST or CST.
const US_ABBR = new Set(['PST', 'PDT', 'MST', 'MDT', 'CST', 'CDT', 'EST', 'EDT', 'AKST', 'AKDT', 'HST', 'UTC']);

function zoneLabel(instant, timeZone) {
  const name = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
    .formatToParts(instant).find(p => p.type === 'timeZoneName')?.value || '';
  if (US_ABBR.has(name)) return name;
  if (name === 'GMT') return 'UTC';
  return name;
}

function fmtParts(instant, timeZone, withDate) {
  const opts = { timeZone, hour: 'numeric', minute: '2-digit', hour12: true };
  if (withDate) Object.assign(opts, { weekday: 'short', month: 'short', day: 'numeric' });
  const text = instant.toLocaleString('en-US', opts);
  return { text, day: instant.toLocaleDateString('en-US', { timeZone }), label: zoneLabel(instant, timeZone) };
}

// "Tue, Oct 21, 10:00 AM PDT" in the event's zone, plus " · 1:00 PM EDT your time"
// when the viewer's zone is known and renders differently. Always labels the zone.
export function formatEventTime(naiveIso, eventTz, viewerTz) {
  if (!naiveIso) return '';
  if (eventTz && !isValidTimezone(eventTz)) return `${naiveIso.replace('T', ' ').slice(0, 16)} (${eventTz})`;
  if (viewerTz && !isValidTimezone(viewerTz)) viewerTz = null;
  const instant = resolveEventInstant(naiveIso, eventTz);
  const ev = fmtParts(instant, eventTz || 'UTC', true);
  const base = `${ev.text} ${ev.label}`;
  if (!viewerTz || viewerTz === (eventTz || 'UTC')) return base;
  const vwTime = fmtParts(instant, viewerTz, false);
  const evTime = fmtParts(instant, eventTz || 'UTC', false);
  const sameDay = vwTime.day === evTime.day;
  if (sameDay && vwTime.text === evTime.text && vwTime.label === evTime.label) return base;
  const shown = sameDay ? vwTime : fmtParts(instant, viewerTz, true);
  return `${base} · ${shown.text} ${shown.label} your time`;
}

export function isValidTimezone(tz) {
  if (!tz || typeof tz !== 'string') return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}
